import React, { useState, useEffect, useRef, useMemo } from 'react'
import { formatPriceFromUSD } from '../../utils/currencyUtils'
import {
  getPaymentStatusBadgeClass,
  groupBookingsForDisplay,
  isOfferDealBooking,
  getDealProfessionalsForReview,
  getDealReviewKey,
  findDealSiblingBookings,
  areAllPackageVisitsCompleted,
  getBookingTaxBreakdown,
  parseAppointmentDateTime,
  getServiceDisplayTimeRange,
} from './dashboardUtils'
import {
  fetchAllPolicyFeeChargesForAppointment,
  fetchAppointmentRefundTotal,
} from '../../services/appointmentPaymentService'

const policyFeeLabel = (paymentType) => {
  if (paymentType === 'reschedule_fee') return 'Reschedule fee'
  if (paymentType === 'cancellation_fee') return 'Cancellation fee'
  return paymentType || 'Fee'
}

const getRescheduleCount = (notes) => {
  const match = String(notes || '').match(/\[RESCHEDULE_COUNT:(\d+)\]/)
  if (!match) return 0
  const n = parseInt(match[1], 10)
  return Number.isNaN(n) ? 0 : n
}

const DETAILS_STATUS_STYLES = {
  confirmed: { bg: '#DBEAFE', text: '#2563EB', label: 'Confirmed' },
  upcoming: { bg: '#DBEAFE', text: '#2563EB', label: 'Confirmed' },
  pending: { bg: '#DBEAFE', text: '#2563EB', label: 'Confirmed' },
  rescheduled: { bg: '#F3E8FF', text: '#7C3AED', label: 'Rescheduled' },
  cancelled: { bg: '#FEE2E2', text: '#DC2626', label: 'Cancelled' },
  completed: { bg: '#D1FAE5', text: '#059669', label: 'Completed' },
  missed: { bg: '#FFEDD5', text: '#EA580C', label: 'Missed' },
}

/** Sort modes for My Bookings — toggleable chips (click again to clear). */
const BOOKINGS_SORT_STORAGE_KEY = 'tts_my_bookings_sort_mode'
const BOOKINGS_SORT_MODES = {
  DEFAULT: 'default',
  LATEST: 'latest',
  DATE_ASC: 'date_asc',
  DATE_DESC: 'date_desc',
}

const readStoredBookingsSortMode = () => {
  try {
    const v = sessionStorage.getItem(BOOKINGS_SORT_STORAGE_KEY)
    if (
      v === BOOKINGS_SORT_MODES.LATEST ||
      v === BOOKINGS_SORT_MODES.DATE_ASC ||
      v === BOOKINGS_SORT_MODES.DATE_DESC
    ) {
      return v
    }
  } catch {
    // ignore
  }
  return BOOKINGS_SORT_MODES.DEFAULT
}

const parseTimestampMs = (raw) => {
  if (raw == null || raw === '') return 0
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (raw instanceof Date) {
    const t = raw.getTime()
    return Number.isFinite(t) ? t : 0
  }
  let s = String(raw).trim()
  if (!s) return 0
  // Normalize Postgres-style "YYYY-MM-DD HH:mm:ss+00" for safer parsing
  if (/^\d{4}-\d{2}-\d{2} /.test(s)) s = s.replace(' ', 'T')
  if (/[+-]\d{2}$/.test(s)) s = `${s}:00`
  const t = new Date(s).getTime()
  return Number.isFinite(t) ? t : 0
}

/** When the user booked (created_at). Future/seed timestamps are ignored so real new bookings win. */
const getBookingCreatedMs = (booking) => {
  if (!booking) return 0
  const raw =
    booking.createdAt ||
    booking.created_at ||
    booking.originalData?.created_at ||
    booking.originalData?.createdAt ||
    null
  const t = parseTimestampMs(raw)
  // Seeded rows sometimes have created_at in the future — ignore so real new bookings win
  if (t > Date.now() + 60_000) return 0
  return t
}

const getDisplayItemCreatedMs = (item) => {
  if (!item) return 0
  if (item.kind === 'deal') {
    let max = 0
    for (const day of item.days || []) {
      const t = getBookingCreatedMs(day)
      if (t > max) max = t
    }
    return max
  }
  return getBookingCreatedMs(item.booking)
}

/** First visit datetime for packages; appointment datetime for singles. */
const getDisplayItemAppointmentMs = (item) => {
  if (!item) return 0
  if (item.kind === 'deal') {
    let min = Number.POSITIVE_INFINITY
    for (const day of item.days || []) {
      const d = parseAppointmentDateTime(day)
      const t = d ? d.getTime() : NaN
      if (Number.isFinite(t) && t < min) min = t
    }
    return Number.isFinite(min) ? min : 0
  }
  const d = parseAppointmentDateTime(item.booking)
  return d ? d.getTime() : 0
}

const sortDisplayItems = (items, sortMode) => {
  if (!items?.length || sortMode === BOOKINGS_SORT_MODES.DEFAULT) return items
  const next = items.slice()
  if (sortMode === BOOKINGS_SORT_MODES.LATEST) {
    next.sort((a, b) => getDisplayItemCreatedMs(b) - getDisplayItemCreatedMs(a))
    return next
  }
  if (sortMode === BOOKINGS_SORT_MODES.DATE_ASC) {
    next.sort((a, b) => getDisplayItemAppointmentMs(a) - getDisplayItemAppointmentMs(b))
    return next
  }
  if (sortMode === BOOKINGS_SORT_MODES.DATE_DESC) {
    next.sort((a, b) => getDisplayItemAppointmentMs(b) - getDisplayItemAppointmentMs(a))
    return next
  }
  return next
}

const getDetailsAppointmentId = (booking) => {
  const ref = booking?.reference_id
  if (ref) return String(ref)
  const id = String(booking?.id || '')
  return id.length >= 8 ? `#${id.slice(0, 8).toUpperCase()}` : '#----'
}

const formatDetailsDate = (value) => {
  if (!value) return ''
  const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

// booking.time is either "HH:MM:SS" from the DB or a { slot: "10:00 AM" } object
const formatDetailsTime = (value) => {
  if (!value) return ''
  const raw = typeof value === 'string' ? value : value.slot || value.time || value.label || ''
  const text = String(raw).trim()
  if (!text) return ''
  if (/am|pm/i.test(text)) return text.toUpperCase().replace(/\s+/g, ' ')

  const parts = text.split(':')
  const hours = parseInt(parts[0] || '', 10)
  const minutes = parseInt(parts[1] || '0', 10)
  if (Number.isNaN(hours)) return text
  const suffix = hours >= 12 ? 'PM' : 'AM'
  const hour12 = ((hours + 11) % 12) + 1
  return `${hour12}:${String(Number.isNaN(minutes) ? 0 : minutes).padStart(2, '0')} ${suffix}`
}

const SummaryRow = ({ label, value, valueColor }) => (
  <div className="flex justify-between items-center py-2.5 gap-3">
    <span className="text-sm font-medium text-[#374151]">{label}</span>
    <span
      className="text-sm font-bold text-right break-words"
      style={{ color: valueColor || '#111827' }}
    >
      {value}
    </span>
  </div>
)

const AppointmentDetailsPanel = ({
  booking,
  currency,
  active,
  total,
  duration,
  parseServiceDuration,
  calculateServiceTimeRange,
  professionalName,
  salonCancellationMessage,
  isCancelled,
  isRefunded,
}) => {
  const [feeRows, setFeeRows] = useState([])
  const [refundAmountUsd, setRefundAmountUsd] = useState(0)

  useEffect(() => {
    if (!active || !booking?.id) {
      setFeeRows([])
      setRefundAmountUsd(0)
      return
    }
    let cancelled = false
    ;(async () => {
      const [feeResult, refundResult] = await Promise.all([
        fetchAllPolicyFeeChargesForAppointment(booking),
        fetchAppointmentRefundTotal(booking),
      ])
      if (cancelled) return
      setFeeRows(Array.isArray(feeResult?.rows) ? feeResult.rows : [])
      setRefundAmountUsd(Number(refundResult?.amountUsd) || 0)
    })()
    return () => {
      cancelled = true
    }
  }, [active, booking?.id, booking?.notes])

  const services = booking.services || []
  const serviceCount = services.length
  const status = String(booking?.status || '').toLowerCase()
  const paymentStatusRaw = String(
    booking?.payment_status || booking?.paymentStatus || '',
  ).toLowerCase()
  const normalizedStatus =
    status === 'canceled' || status === 'cancelled'
      ? 'cancelled'
      : status === 'no_show'
        ? 'missed'
        : status
  // Terminal statuses win so Cancelled tab details always show Cancelled (same as list context)
  const statusKey =
    normalizedStatus === 'cancelled' ||
    normalizedStatus === 'completed' ||
    normalizedStatus === 'missed'
      ? normalizedStatus
      : booking?.rescheduleTag
        ? 'rescheduled'
        : normalizedStatus || 'upcoming'
  const statusStyle = DETAILS_STATUS_STYLES[statusKey] || DETAILS_STATUS_STYLES.confirmed
  const rescheduleCount = getRescheduleCount(booking.notes)

  const paymentMethod = booking.payment_method || booking.paymentMethod || 'cash'
  const paymentMethodSuffix = paymentMethod === 'card' ? ' (Card)' : ' (Cash)'
  const paymentStatusLabel =
    paymentStatusRaw === 'refunded'
      ? 'Refunded'
      : paymentStatusRaw === 'paid'
        ? 'Paid'
        : 'Unpaid'
  const paymentStatusColor =
    paymentStatusRaw === 'refunded'
      ? '#6B7280'
      : paymentStatusRaw === 'paid'
        ? '#059669'
        : '#EA580C'
  const paymentStatusDisplay =
    paymentStatusRaw === 'refunded'
      ? paymentStatusLabel
      : `${paymentStatusLabel}${paymentMethodSuffix}`

  const showSalonMessage = isCancelled || isRefunded || !!salonCancellationMessage

  // Scroll the services list once it gets long instead of stretching the card
  const scrollServices = serviceCount > 3
  const taxBreakdown = getBookingTaxBreakdown(booking, total)

  const getServiceProfessional = (service) => {
    const forcedPrimaryName =
      serviceCount === 1 && booking.professionalData?.primary?.name
        ? booking.professionalData.primary.name
        : null
    const name =
      forcedPrimaryName ||
      service.professional?.name ||
      (booking.professionalData?.professionals || []).find(
        (p) => p && (p.id === service.professional_id || p.id === service.professional?.id),
      )?.name ||
      booking.professionalData?.primary?.name ||
      (booking.professionalData?.professionals || [])[0]?.name ||
      booking.specialist?.name ||
      professionalName
    if (!name || name === 'N/A') return null
    const role =
      service.professional?.role ||
      service.professional?.specialty ||
      booking.specialist?.role ||
      booking.specialist?.specialty
    return role ? `with ${name} - ${role}` : `with ${name}`
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* Summary */}
      <div className="px-4 pt-4 pb-3 border-b border-[#F3F4F6]">
        <div className="flex items-center gap-2 flex-wrap mb-2.5">
          <span className="text-[15px] font-bold text-[#111827]">
            {getDetailsAppointmentId(booking)}
          </span>
          {/* Skip Cancelled/Completed here — already shown once on the card/package header */}
          {statusKey !== 'cancelled' && statusKey !== 'completed' ? (
            <span
              className="text-xs font-semibold px-2.5 py-1 rounded-md"
              style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
            >
              {statusStyle.label}
            </span>
          ) : null}
        </div>
        <p className="text-[15px] font-semibold text-[#111827] leading-[22px] mb-1.5">
          {formatDetailsDate(booking.date)} • {formatDetailsTime(booking.time)}
        </p>
        <p className="text-[13px] text-[#6B7280] leading-5">
          {serviceCount} service{serviceCount !== 1 ? 's' : ''} •{' '}
          {formatPriceFromUSD(total, currency)} • {duration} min
        </p>
        {rescheduleCount > 0 ? (
          <p className="text-[13px] text-[#6B7280] leading-5 mt-1">
            Rescheduled {rescheduleCount} time{rescheduleCount === 1 ? '' : 's'}
          </p>
        ) : null}
      </div>

      {/* Services — scrolls when there are many */}
      <div
        className={`bg-[#F9FAFB] px-4 py-3 ${
          scrollServices ? 'max-h-[260px] overflow-y-auto overscroll-contain' : ''
        }`}
      >
        {services.map((service, index) => {
          let cumulativeDuration = 0
          for (let i = 0; i < index; i++) {
            const prev = services[i]
            cumulativeDuration += parseServiceDuration
              ? (prev.duration_minutes || parseServiceDuration(prev.duration) || parseServiceDuration(prev))
              : 0
          }
          const serviceDuration = parseServiceDuration
            ? (service.duration_minutes || parseServiceDuration(service.duration) || parseServiceDuration(service))
            : 0
          // Prefer stored per-service schedule (book-by-service-time); fall back to stacked times
          const { start, end } = getServiceDisplayTimeRange(
            service,
            booking.time,
            cumulativeDuration,
            serviceDuration
          )
          const proLabel = getServiceProfessional(service)

          return (
            <div
              key={service.id || index}
              className={`bg-[#F3F4F6] rounded-xl px-3.5 py-3.5 ${
                index === services.length - 1 ? '' : 'mb-2.5'
              }`}
            >
              <div className="flex items-start justify-between gap-3 mb-1.5">
                <p className="flex-1 min-w-0 text-[15px] font-bold text-[#111827] leading-[21px]">
                  {typeof service === 'string'
                    ? service
                    : (service?.name || 'Service')}
                </p>
                <p className="text-[15px] font-bold text-[#8B5CF6] shrink-0">
                  {formatPriceFromUSD(service.price || 0, currency)}
                </p>
              </div>
              {proLabel ? (
                <p className="text-[13px] text-[#6B7280] leading-[19px] mt-0.5">{proLabel}</p>
              ) : null}
              {start && end ? (
                <p className="text-[13px] text-[#6B7280] leading-[19px] mt-0.5">
                  {start} - {end}
                </p>
              ) : null}
            </div>
          )
        })}

        {showSalonMessage ? (
          <div className="bg-[#F3F4F6] rounded-xl p-3.5 mt-2.5">
            <p className="text-[13px] font-medium text-[#6B7280] mb-1.5">Salon Message</p>
            <p className="text-[13px] text-[#111827] leading-[19px]">
              {salonCancellationMessage || 'No cancellation message was provided.'}
            </p>
          </div>
        ) : null}
      </div>

      {/* Payment */}
      <div className="px-4 pt-2 pb-4 border-t border-[#E5E7EB]">
        {taxBreakdown.subtotal > 0 && taxBreakdown.tax > 0 ? (
          <SummaryRow
            label="Subtotal"
            value={formatPriceFromUSD(taxBreakdown.subtotal, currency)}
          />
        ) : null}
        {taxBreakdown.discount > 0 ? (
          <SummaryRow
            label="Discount"
            value={`-${formatPriceFromUSD(taxBreakdown.discount, currency)}`}
            valueColor="#059669"
          />
        ) : null}
        {taxBreakdown.tax > 0 ? (
          <SummaryRow
            label="Tax"
            value={formatPriceFromUSD(taxBreakdown.tax, currency)}
          />
        ) : null}
        <SummaryRow
          label="Total Cost"
          value={formatPriceFromUSD(total, currency)}
          valueColor="#8B5CF6"
        />
          {feeRows.map((row) => (
          <SummaryRow
              key={row.id || `fee-${row.payment_type}`}
              label={policyFeeLabel(row.payment_type)}
            value={formatPriceFromUSD(Number(row.amount_usd) || 0, currency)}
            />
          ))}
        {isRefunded && refundAmountUsd > 0 ? (
          <SummaryRow
            label="Refunded Amount"
            value={formatPriceFromUSD(refundAmountUsd, currency)}
            valueColor="#6B7280"
          />
        ) : null}
        <SummaryRow
          label="Payment Status"
          value={paymentStatusDisplay}
          valueColor={paymentStatusColor}
        />
        {booking.payment_reference_id ? (
          <p className="text-xs text-[#9CA3AF] mt-1 text-right break-all">
            {booking.payment_reference_id}
          </p>
        ) : null}
        </div>
    </div>
  )
}

const BookingsTab = ({
  bookingFilter,
  setBookingFilter,
  upcomingBookings,
  completedBookings,
  cancelledBookings,
  missedBookings,
  allBookings = [],
  appointments,
  refreshingAppointments,
  refreshAppointments,
  getFilteredBookings,
  calculateBookingTotal,
  calculateBookingDuration,
  formatDateForDisplay,
  formatTimeForDisplay,
  getPaymentStatus,
  isAppointmentExpired,
  isAppointmentAtLeastOneHourAway,
  canCancelAppointment,
  canRescheduleAppointment,
  getRescheduleBlockReason,
  getBookingProfessionalDisplay,
  getBookingProfessionalsForReview,
  getDealProfessionalsForReview: getDealProfessionalsForReviewProp,
  getDealReviewKey: getDealReviewKeyProp,
  parseServiceDuration,
  calculateServiceTimeRange,
  handleCancelAppointment,
  handleRemoveBooking,
  handleClearAllCancelled,
  handleReschedule,
  toggleBookingCollapse,
  collapsedBookings,
  toggleReview,
  handleReviewRating,
  handleReviewComment,
  handleSubmitReview,
  showReviewForBooking,
  reviewData,
  activeReviewTab,
  setActiveReviewProfessional,
  submittingReviewBookingId,
  cancellingBookingId,
  currency,
  onBookNow,
  handleBookAgain,
  onCloseSidebar
}) => {
  const [expandedDetails, setExpandedDetails] = useState({})
  const [expandedDeals, setExpandedDeals] = useState({})
  const [checkingRescheduleFee, setCheckingRescheduleFee] = useState(null) // Track which booking is being checked
  const [sortMode, setSortMode] = useState(readStoredBookingsSortMode)
  const refreshAppointmentsRef = useRef(refreshAppointments)

  const toggleSortMode = (mode) => {
    setSortMode((prev) => {
      const next = prev === mode ? BOOKINGS_SORT_MODES.DEFAULT : mode
      try {
        if (next === BOOKINGS_SORT_MODES.DEFAULT) {
          sessionStorage.removeItem(BOOKINGS_SORT_STORAGE_KEY)
        } else {
          sessionStorage.setItem(BOOKINGS_SORT_STORAGE_KEY, next)
        }
      } catch {
        // ignore storage errors
      }
      return next
    })
  }

  const upcomingCount = useMemo(
    () => groupBookingsForDisplay(upcomingBookings || []).length,
    [upcomingBookings]
  )
  const completedCount = useMemo(
    () => groupBookingsForDisplay(completedBookings || []).length,
    [completedBookings]
  )
  const cancelledCount = useMemo(
    () => groupBookingsForDisplay(cancelledBookings || []).length,
    [cancelledBookings]
  )
  const missedCount = useMemo(
    () => groupBookingsForDisplay(missedBookings || []).length,
    [missedBookings]
  )
  const displayItems = useMemo(() => {
    const grouped = groupBookingsForDisplay(getFilteredBookings() || [])
    return sortDisplayItems(grouped, sortMode)
    // getFilteredBookings is recreated each parent render; depend on filter lists instead
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bookingFilter,
    upcomingBookings,
    completedBookings,
    cancelledBookings,
    missedBookings,
    sortMode,
  ])

  const toggleDealExpand = (dealId) => {
    setExpandedDeals((prev) => ({
      ...prev,
      [dealId]: !prev[dealId],
    }))
  }

  // Packages start collapsed (same as app); user taps "Show days" to expand
  const isDealExpanded = (dealId) => !!expandedDeals[dealId]

  useEffect(() => {
    refreshAppointmentsRef.current = refreshAppointments
  }, [refreshAppointments])

  // Refresh appointments when switching to completed tab to ensure reviews are loaded
  // Depend only on bookingFilter — refreshAppointments identity changes every parent render
  useEffect(() => {
    if (bookingFilter === 'completed' && refreshAppointmentsRef.current) {
      refreshAppointmentsRef.current()
    }
  }, [bookingFilter])

  const pendingDeepLinkRef = useRef(null)

  // Deep link from a notification: switch tab, expand that appointment, scroll to it
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('openAppointmentDetails')
      if (raw) {
        sessionStorage.removeItem('openAppointmentDetails')
        pendingDeepLinkRef.current = JSON.parse(raw)
      }
    } catch {
      // ignore storage/parse errors
    }

    const payload = pendingDeepLinkRef.current
    if (!payload?.appointmentId) return

    const allKnown = [
      ...(appointments || []),
      ...(upcomingBookings || []),
      ...(completedBookings || []),
      ...(cancelledBookings || []),
      ...(missedBookings || []),
      ...(getFilteredBookings?.() || []),
    ]

    const linkedBooking =
      allKnown.find((a) => String(a.id) === String(payload.appointmentId)) ||
      allKnown.find(
        (a) =>
          String(a.group_booking_id || a.groupBookingId || '') ===
          String(payload.appointmentId),
      ) ||
      null

    // If we know the real status, prefer that tab over the notification default
    if (linkedBooking?.status) {
      const status = String(linkedBooking.status).toLowerCase()
      if (status === 'cancelled' || status === 'canceled') {
        setBookingFilter('cancelled')
      } else if (status === 'completed') {
        setBookingFilter('completed')
      } else if (status === 'missed' || status === 'no_show') {
        setBookingFilter('missed')
      } else if (payload.bookingFilter) {
        setBookingFilter(payload.bookingFilter)
      }
    } else if (payload.bookingFilter) {
      setBookingFilter(payload.bookingFilter)
    }

    const targetId = linkedBooking?.id || payload.appointmentId
    if (payload.openDetails) {
      setExpandedDetails((prev) => ({ ...prev, [targetId]: true }))
    }

    const groupId = linkedBooking?.group_booking_id || linkedBooking?.groupBookingId
    if (groupId) {
      setExpandedDeals((prev) => ({ ...prev, [groupId]: true }))
    }

    const timer = setTimeout(() => {
      const el =
        document.getElementById(`booking-card-${targetId}`) ||
        document.getElementById(`booking-card-${payload.appointmentId}`) ||
        (groupId ? document.getElementById(`deal-card-${groupId}`) : null)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        pendingDeepLinkRef.current = null
      }
    }, 350)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setBookingFilter, appointments, upcomingBookings, completedBookings, cancelledBookings, missedBookings])

  const toggleDetails = (bookingId) => {
    setExpandedDetails(prev => ({
      ...prev,
      [bookingId]: !prev[bookingId]
    }))
  }

  const filterBorderClass =
    bookingFilter === 'missed'
      ? 'border-red-500'
              : bookingFilter === 'cancelled'
        ? 'border-gray-400'
        : bookingFilter === 'completed'
          ? 'border-green-500'
          : 'border-primary'

  const renderBookingCard = (booking, { cardTitle = 'Appointment', nested = false, inDeal = false } = {}) => {
            const total = calculateBookingTotal(booking)
            const duration = calculateBookingDuration(booking)
            const formattedDate = formatDateForDisplay(booking.date)
            const formattedTime = formatTimeForDisplay(booking.time)
            const showReview = showReviewForBooking[booking.id]
            const serviceList = booking.services
              ?.map((service) =>
                typeof service === 'string' ? service : (service?.name || 'Service')
              )
              .join(', ')
            const isCollapsed = collapsedBookings[booking.id]
            const paymentStatus = getPaymentStatus(booking)
    const serviceCount = booking.services?.length || 0
            
            const dbAppointment = appointments?.find(apt => apt.id === booking.id)
    const reviewProfessionals = getBookingProfessionalsForReview
      ? getBookingProfessionalsForReview(booking)
      : []
    const existingReviews = Array.isArray(dbAppointment?.reviews)
      ? dbAppointment.reviews
      : Array.isArray(booking.reviews)
        ? booking.reviews
        : []
    const reviewedProfessionalIds = new Set(
      existingReviews
        .map((r) => (r?.professional_id != null ? String(r.professional_id) : null))
        .filter(Boolean)
    )
    const hasReview =
      reviewProfessionals.length > 0
        ? reviewProfessionals.every((p) => reviewedProfessionalIds.has(String(p.id)))
        : !!(dbAppointment?.review || booking.review)
    const activeProfId =
      activeReviewTab?.[booking.id] ||
      reviewProfessionals[0]?.id ||
      null
    const draftReview =
      (activeProfId && reviewData?.[booking.id]?.[activeProfId]) || { rating: 0, comment: '' }
    const activeSavedReview =
      existingReviews.find((r) => String(r.professional_id) === String(activeProfId)) ||
      existingReviews[0] ||
      dbAppointment?.review ||
      booking.review ||
      null
    const activeProfName =
      reviewProfessionals.find((p) => String(p.id) === String(activeProfId))?.name ||
      (getBookingProfessionalDisplay ? getBookingProfessionalDisplay(booking) : 'the professional')
            
            const isDetailsExpanded = expandedDetails[booking.id]
            const isCancelled = String(booking?.status || '').toLowerCase() === 'cancelled'
            const isRefunded =
              String(booking?.payment_status || booking?.paymentStatus || '').toLowerCase() === 'refunded'
            const salonCancellationMessage =
              typeof booking?.admin_cancellation_reason === 'string'
                ? booking.admin_cancellation_reason.trim()
                : ''
            const professionalName = getBookingProfessionalDisplay
              ? getBookingProfessionalDisplay(booking)
              : (booking.specialist?.name || 'Specialist')

    const priceLine = inDeal
      ? `Included in package · ${serviceCount} service${serviceCount !== 1 ? 's' : ''} · ${duration}min`
      : `${formatPriceFromUSD(total, currency)} • ${serviceCount} service${serviceCount !== 1 ? 's' : ''} • ${duration}min`
            
            return (
              <div
                key={booking.id}
        id={`booking-card-${booking.id}`}
        className={
          nested
            ? 'bg-gray-50 rounded-lg border border-gray-200'
            : `bg-white rounded-lg border-l-4 shadow-sm hover:shadow-md transition ${filterBorderClass}`
        }
              >
                <div className="p-3 sm:p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-base text-gray-900">{cardTitle}</h3>
                {bookingFilter === 'cancelled' && !inDeal && (
                  <span className="inline-flex items-center text-[10px] font-bold text-red-700 bg-red-50 px-2 py-0.5 rounded-full border border-red-200">
                    Cancelled
                  </span>
                )}
                {bookingFilter === 'completed' && !inDeal && (
                  <span className="inline-flex items-center text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                    Completed
                  </span>
                )}
                        {booking.rescheduleTag && (
                          <span className="inline-flex items-center text-[10px] font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-200">
                            Rescheduled
                          </span>
                        )}
                        <span
                          className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full ${getPaymentStatusBadgeClass(booking)}`}
                        >
                          {paymentStatus}
                        </span>
                      </div>
                      {isCollapsed && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {serviceList || 'Selected services'} • {formattedDate}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 flex-wrap justify-end">
              {bookingFilter === 'completed' && !inDeal && (
                        <>
                          {!hasReview && !showReview && (
                        <button
                          onClick={() => toggleReview(booking.id)}
                      className="bg-secondary text-white py-1.5 px-3 rounded-lg hover:bg-gray-800 transition font-medium text-sm inline-flex items-center gap-1.5"
                        >
                      <i className="far fa-star text-xs"></i>
                      Leave a review
                        </button>
                      )}
                          {hasReview && (
                        <button
                          onClick={() => toggleReview(booking.id)}
                      className="bg-emerald-50 text-emerald-700 border border-emerald-200 py-1.5 px-3 rounded-lg hover:bg-emerald-100 transition font-medium text-sm inline-flex items-center gap-1.5"
                        >
                      <i className="fas fa-check-circle text-xs"></i>
                      {showReview ? 'Hide review' : 'Reviewed'}
                        </button>
                          )}
                        </>
                      )}
                      
                      {bookingFilter !== 'upcoming' && bookingFilter !== 'missed' && !appointments?.some(apt => apt.id === booking.id) && (
                        <button
                          onClick={() => handleRemoveBooking(booking.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 text-gray-600 hover:text-red-500 hover:border-red-500 transition"
                          aria-label="Remove appointment"
                        >
                          <i className="fas fa-times text-sm"></i>
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {isCollapsed ? (
                    <div className="bg-gray-50 rounded-lg p-3 mt-2 text-xs text-gray-700">
                      <p className="font-medium text-gray-900 mb-0.5">{serviceList || 'Selected services'}</p>
                      <p className="mb-0.5">{formattedDate} at {formattedTime}</p>
                      <p className="mb-0.5">
                        {getBookingProfessionalDisplay
                          ? getBookingProfessionalDisplay(booking)
                          : (booking.specialist?.name || 'Specialist')}
                      </p>
              <p>
                {inDeal
                  ? `Included in package · ${serviceCount} service${serviceCount !== 1 ? 's' : ''}`
                  : `${formatPriceFromUSD(total, currency)} • ${serviceCount} service${serviceCount !== 1 ? 's' : ''}`}
              </p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-1.5 mb-3">
                        <p className="text-gray-600 text-xs flex items-center gap-1.5">
                          <i className="far fa-calendar text-gray-400 w-3.5 text-center"></i>
                          {formattedDate} at {formattedTime}
                        </p>
                        <p className="text-gray-600 text-xs flex items-center gap-1.5">
                          <i className="fas fa-tag text-gray-400 w-3.5 text-center"></i>
                  {priceLine}
                        </p>
                      </div>
                      
                      {isDetailsExpanded && (
                <div className="mb-3">
                  <AppointmentDetailsPanel
                            booking={booking}
                            currency={currency}
                            active={isDetailsExpanded}
                    total={inDeal
                      ? (booking.services || []).reduce((s, x) => s + parseFloat(x.price || 0), 0)
                      : total}
                    duration={duration}
                    parseServiceDuration={parseServiceDuration}
                    calculateServiceTimeRange={calculateServiceTimeRange}
                    professionalName={professionalName}
                            salonCancellationMessage={salonCancellationMessage}
                            isCancelled={isCancelled}
                            isRefunded={isRefunded}
                          />
                        </div>
                      )}
                      
              {bookingFilter === 'completed' && !inDeal && (
                        <>
                          {hasReview && showReview ? (
                            <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
                              <div className="flex justify-between items-center mb-4">
                        <h4 className="font-semibold text-gray-900">Your review</h4>
                                <button
                                  onClick={() => toggleReview(booking.id)}
                                  className="text-primary hover:underline text-sm font-medium"
                                >
                          Hide review
                                </button>
                              </div>

                      {reviewProfessionals.length > 1 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {reviewProfessionals.map((prof) => {
                            const isActive = String(prof.id) === String(activeProfId)
                            return (
                              <button
                                key={prof.id}
                                type="button"
                                onClick={() => setActiveReviewProfessional?.(booking.id, prof.id)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                                  isActive
                                    ? 'bg-secondary text-white border-secondary'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                                }`}
                              >
                                {prof.name}
                              </button>
                            )
                          })}
                        </div>
                      )}
                              
                              <p className="text-gray-700 mb-3">
                        How was your experience with {activeProfName}?
                              </p>
                              
                              <div className="flex gap-1 mb-4">
                                {[1, 2, 3, 4, 5].map((star) => (
                          <div key={star} className="text-2xl">
                                    <i
                                      className={`${
                                star <= (activeSavedReview?.rating || 0)
                                          ? 'fas fa-star text-yellow-400'
                                          : 'far fa-star text-gray-300'
                                      }`}
                                    ></i>
                                  </div>
                                ))}
                              </div>
                              
                              <textarea
                        value={activeSavedReview?.comment || ''}
                                disabled
                                placeholder="Share your experience with this service..."
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 resize-none mb-4 cursor-not-allowed"
                                rows="3"
                              ></textarea>
                            </div>
                          ) : showReview ? (
                            <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
                              <div className="flex justify-between items-center mb-4">
                        <h4 className="font-semibold text-gray-900">
                          {reviewProfessionals.length > 1 ? 'Leave reviews' : 'Leave a review'}
                        </h4>
                                <button
                                  onClick={() => toggleReview(booking.id)}
                                  className="text-primary hover:underline text-sm font-medium"
                                >
                          Hide review
                                </button>
                              </div>

                      {reviewProfessionals.length > 1 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {reviewProfessionals.map((prof) => {
                            const isActive = String(prof.id) === String(activeProfId)
                            const draft = reviewData?.[booking.id]?.[prof.id] || {}
                            const hasDraft =
                              (draft.rating || 0) > 0 || String(draft.comment || '').trim().length > 0
                            return (
                              <button
                                key={prof.id}
                                type="button"
                                onClick={() => setActiveReviewProfessional?.(booking.id, prof.id)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                                  isActive
                                    ? 'bg-secondary text-white border-secondary'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                                }`}
                              >
                                {prof.name}{hasDraft ? ' •' : ''}
                              </button>
                            )
                          })}
                        </div>
                      )}
                              
                              <p className="text-gray-700 mb-3">
                        How was your experience with {activeProfName}?
                              </p>
                              
                              <div className="flex gap-1 mb-4">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <button
                                    key={star}
                                    type="button"
                            onClick={() => handleReviewRating(booking.id, star, activeProfId)}
                                    className="text-2xl focus:outline-none hover:scale-110 transition-transform cursor-pointer"
                                    aria-label={`Rate ${star} star${star !== 1 ? 's' : ''}`}
                            disabled={!activeProfId}
                                  >
                                    <i
                                      className={`${
                                star <= (draftReview.rating || 0)
                                          ? 'fas fa-star text-yellow-400'
                                          : 'far fa-star text-gray-300'
                                      }`}
                                    ></i>
                                  </button>
                                ))}
                              </div>
                              
                              <textarea
                        value={draftReview.comment || ''}
                        onChange={(e) => handleReviewComment(booking.id, e.target.value, activeProfId)}
                        placeholder={`Share your experience with ${activeProfName}...`}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none mb-4"
                                rows="3"
                        disabled={!activeProfId}
                              ></textarea>

                      {reviewProfessionals.length > 1 && (
                        <p className="text-xs text-gray-500 mb-3">
                          Switch tabs to review each professional, then submit all at once.
                        </p>
                      )}
                              
                              <button
                                onClick={() => handleSubmitReview(booking.id)}
                        disabled={submittingReviewBookingId === booking.id || !activeProfId}
                                className={`bg-secondary text-white py-1.5 px-3 rounded-lg transition font-medium text-sm flex items-center gap-2 ${
                                  submittingReviewBookingId === booking.id 
                                    ? 'opacity-50 cursor-not-allowed' 
                                    : 'hover:bg-gray-800'
                                }`}
                              >
                                {submittingReviewBookingId === booking.id ? (
                                  <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    <span>Submitting...</span>
                                  </>
                        ) : reviewProfessionals.length > 1 ? (
                          `Submit ${reviewProfessionals.length} reviews`
                                ) : (
                          'Submit review'
                                )}
                              </button>
                            </div>
                          ) : null}
                        </>
                      )}
                    </>
                  )}
                  
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <button
                      onClick={() => toggleDetails(booking.id)}
                      className="border border-gray-300 text-gray-700 py-1.5 px-3 rounded-lg hover:bg-gray-50 transition font-medium text-xs sm:text-sm flex items-center gap-1"
                    >
                      <i className={`fas fa-${isDetailsExpanded ? 'chevron-up' : 'chevron-down'} text-xs`}></i>
                      {isDetailsExpanded ? 'Hide Details' : 'View Details'}
                    </button>
                    
            {bookingFilter === 'upcoming' && !inDeal && (booking.status === 'upcoming' || booking.status === 'confirmed' || booking.status === 'pending') && !isAppointmentExpired(booking) && (
                      (() => {
                        const isCancelling = cancellingBookingId === booking.id
                        return (
                      <button
                        onClick={() => handleCancelAppointment(booking.id)}
                        disabled={!canCancelAppointment || !canCancelAppointment(booking) || isCancelling}
                        className={`py-1.5 px-3 rounded-lg transition font-medium text-xs sm:text-sm flex items-center gap-1 ${
                          isCancelling
                            ? 'bg-red-50 text-red-600 cursor-wait opacity-70'
                            : canCancelAppointment && canCancelAppointment(booking)
                            ? 'bg-red-50 text-red-600 hover:bg-red-100'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                        title={
                          isCancelling
                            ? 'Cancelling this appointment...'
                            : !canCancelAppointment || !canCancelAppointment(booking)
                            ? 'Cancellation is not available for this appointment'
                            : ''
                        }
                      >
                        {isCancelling ? (
                          <>
                            <i className="fas fa-spinner fa-spin text-xs"></i>
                            Cancelling...
                          </>
                        ) : (
                          <>
                        <i className="fas fa-times text-xs"></i>
                        Cancel
                          </>
                        )}
                      </button>
                        )
                      })()
                    )}
                    
            {bookingFilter === 'upcoming' && !inDeal && (booking.status === 'upcoming' || booking.status === 'confirmed' || booking.status === 'pending') && !isAppointmentExpired(booking) && (
                      <button
                        onClick={async () => {
                          if (handleReschedule && !checkingRescheduleFee) {
                            setCheckingRescheduleFee(booking.id)
                            try {
                              await handleReschedule(booking, onCloseSidebar)
                            } catch (error) {
                              console.error('Error in handleReschedule:', error)
                            } finally {
                              setTimeout(() => setCheckingRescheduleFee(null), 500)
                            }
                          }
                        }}
                        disabled={checkingRescheduleFee === booking.id}
                        className={`py-1.5 px-3 rounded-lg transition font-medium text-xs sm:text-sm flex items-center gap-1 ${
                          checkingRescheduleFee === booking.id
                            ? 'bg-blue-100 text-blue-700 cursor-wait opacity-70'
                            : canRescheduleAppointment && canRescheduleAppointment(booking)
                            ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                            : 'bg-gray-100 text-gray-400 cursor-pointer'
                        }`}
                        title={
                          checkingRescheduleFee === booking.id
                            ? 'Checking if reschedule fee was already paid...'
                            : !canRescheduleAppointment || !canRescheduleAppointment(booking)
                            ? 'Click to see why rescheduling is not available'
                            : ''
                        }
                      >
                        {checkingRescheduleFee === booking.id ? (
                          <>
                            <i className="fas fa-spinner fa-spin text-xs"></i>
                            Checking...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-calendar-alt text-xs"></i>
                            Reschedule
                          </>
                        )}
                      </button>
                    )}
                    
            {(bookingFilter === 'completed' || bookingFilter === 'cancelled') &&
              !isOfferDealBooking(booking) &&
              !(inDeal && booking?.offer_id) && (
                      <button
                        onClick={() => {
                          if (handleBookAgain) {
                            handleBookAgain(booking)
                          } else if (onBookNow) {
                            onBookNow()
                          }
                        }}
                        className="bg-primary text-white py-1.5 px-3 rounded-lg hover:bg-primary/90 transition font-medium text-xs sm:text-sm flex items-center gap-1"
                      >
                        <i className="fas fa-plus text-xs"></i>
                        Book Again
                      </button>
                    )}
                    
                    {bookingFilter === 'upcoming' && (booking.status === 'upcoming' || booking.status === 'confirmed' || booking.status === 'pending') && !isAppointmentExpired(booking) && (() => {
              const canCancel = canCancelAppointment ? canCancelAppointment(booking) : true
              const canReschedule = canRescheduleAppointment ? canRescheduleAppointment(booking) : true
              const bothBlocked = canCancelAppointment && canRescheduleAppointment && !canCancel && !canReschedule
              const onlyCancelBlocked = canCancelAppointment && !canCancel && canReschedule
              const onlyRescheduleBlocked = canRescheduleAppointment && !canReschedule && canCancel
                      
                      if (bothBlocked) {
                        return (
                          <div className="w-full text-center mt-2">
                            <p className="text-sm text-red-600 font-medium">
                      {(booking?.cancel_allowed === false ||
                        booking?.allow_cancel === false ||
                        booking?.reschedule_allowed === false ||
                        booking?.allow_reschedule === false ||
                        booking?.deal?.cancel_allowed === false ||
                        booking?.deal?.allow_cancel === false)
                        ? 'Cancellation and rescheduling are turned off for this offer'
                        : 'Cancellation/rescheduling is not available for this appointment'}
                            </p>
                          </div>
                )
              }
              if (onlyCancelBlocked) {
                        return (
                          <div className="w-full text-center mt-2">
                            <p className="text-sm text-red-600 font-medium">
                      {(booking?.cancel_allowed === false ||
                        booking?.allow_cancel === false ||
                        booking?.deal?.cancel_allowed === false ||
                        booking?.deal?.allow_cancel === false)
                        ? 'Cancellation is turned off for this offer'
                        : 'Cancellation is not available for this appointment'}
                            </p>
                          </div>
                )
              }
              if (onlyRescheduleBlocked) {
                const blockReason = getRescheduleBlockReason ? getRescheduleBlockReason(booking) : null
                let message = 'Rescheduling is not available for this appointment'
                if (blockReason === 'offerBlocked') {
                  message = 'Rescheduling is turned off for this offer'
                } else if (blockReason === 'maxReschedules') {
                  message = 'You have already used all allowed reschedules for this appointment.'
                        } else if (blockReason === 'cutoff') {
                  message = 'Rescheduling is not available this close to the appointment time.'
                        }
                        return (
                          <div className="w-full text-center mt-2">
                    <p className="text-sm text-red-600 font-medium">{message}</p>
                  </div>
                )
              }
              return null
            })()}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-0">
      <div className="mb-4 px-4">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Appointments</h2>
      </div>
      
      {/* Filter Tabs */}
      <div className="flex gap-2 mb-4 px-4 border-b border-gray-200 overflow-x-auto">
        <button
          onClick={() => setBookingFilter('upcoming')}
          className={`px-3 py-2 text-sm font-semibold transition relative whitespace-nowrap ${
            bookingFilter === 'upcoming'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-600 hover:text-primary'
          }`}
        >
          Upcoming
          {upcomingCount > 0 && (
            <span className="ml-1.5 bg-secondary text-white text-xs rounded-full px-1.5 py-0.5">
              {upcomingCount}
            </span>
          )}
        </button>
        
        <button
          onClick={() => setBookingFilter('completed')}
          className={`px-3 py-2 text-sm font-semibold transition relative whitespace-nowrap ${
            bookingFilter === 'completed'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-600 hover:text-primary'
          }`}
        >
          Completed
          {completedCount > 0 && (
            <span className="ml-1.5 bg-secondary text-white text-xs rounded-full px-1.5 py-0.5">
              {completedCount}
            </span>
          )}
        </button>
        
        <button
          onClick={() => setBookingFilter('cancelled')}
          className={`px-3 py-2 text-sm font-semibold transition relative whitespace-nowrap ${
            bookingFilter === 'cancelled'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-600 hover:text-primary'
          }`}
        >
          Cancelled
          {cancelledCount > 0 && (
            <span className="ml-1.5 bg-secondary text-white text-xs rounded-full px-1.5 py-0.5">
              {cancelledCount}
            </span>
          )}
        </button>
        
        <button
          onClick={() => setBookingFilter('missed')}
          className={`px-3 py-2 text-sm font-semibold transition relative whitespace-nowrap ${
            bookingFilter === 'missed'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-600 hover:text-primary'
          }`}
        >
          Missed
          {missedCount > 0 && (
            <span className="ml-1.5 bg-secondary text-white text-xs rounded-full px-1.5 py-0.5">
              {missedCount}
            </span>
          )}
        </button>
      </div>

      {/* Sort controls — click to activate, click again to clear (default tab order) */}
      <div className="flex flex-wrap items-center gap-2 mb-3 px-4">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-1">
          Sort
        </span>
        <button
          type="button"
          onClick={() => toggleSortMode(BOOKINGS_SORT_MODES.LATEST)}
          aria-pressed={sortMode === BOOKINGS_SORT_MODES.LATEST}
          title="Show most recently booked first. Click again to clear."
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
            sortMode === BOOKINGS_SORT_MODES.LATEST
              ? 'bg-primary text-white border-primary shadow-sm'
              : 'bg-white text-gray-700 border-gray-200 hover:border-primary hover:text-primary'
          }`}
        >
          <i className="fas fa-clock" aria-hidden="true" />
          Latest booked
        </button>
        <button
          type="button"
          onClick={() => toggleSortMode(BOOKINGS_SORT_MODES.DATE_ASC)}
          aria-pressed={sortMode === BOOKINGS_SORT_MODES.DATE_ASC}
          title="Sort by appointment date & time, oldest / soonest first. Click again to clear."
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
            sortMode === BOOKINGS_SORT_MODES.DATE_ASC
              ? 'bg-primary text-white border-primary shadow-sm'
              : 'bg-white text-gray-700 border-gray-200 hover:border-primary hover:text-primary'
          }`}
        >
          <i className="fas fa-sort-amount-up" aria-hidden="true" />
          Date ASC
        </button>
        <button
          type="button"
          onClick={() => toggleSortMode(BOOKINGS_SORT_MODES.DATE_DESC)}
          aria-pressed={sortMode === BOOKINGS_SORT_MODES.DATE_DESC}
          title="Sort by appointment date & time, newest / latest first. Click again to clear."
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
            sortMode === BOOKINGS_SORT_MODES.DATE_DESC
              ? 'bg-primary text-white border-primary shadow-sm'
              : 'bg-white text-gray-700 border-gray-200 hover:border-primary hover:text-primary'
          }`}
        >
          <i className="fas fa-sort-amount-down" aria-hidden="true" />
          Date DESC
        </button>
        {sortMode !== BOOKINGS_SORT_MODES.DEFAULT && (
          <span className="text-xs text-gray-400">
            {sortMode === BOOKINGS_SORT_MODES.LATEST
              ? 'Recently booked first'
              : sortMode === BOOKINGS_SORT_MODES.DATE_ASC
                ? 'Soonest date first'
                : 'Latest date first'}
          </span>
        )}
      </div>
      
    
      
      {displayItems.length === 0 ? (
        <div className="text-center py-12">
          <i className="fas fa-calendar-times text-6xl text-gray-300 mb-4"></i>
          <p className="text-gray-600 text-lg mb-4">
            {bookingFilter === 'upcoming' 
              ? 'No upcoming bookings yet' 
              : bookingFilter === 'completed'
              ? 'No completed bookings yet'
              : bookingFilter === 'cancelled'
              ? 'No cancelled bookings yet'
              : bookingFilter === 'missed'
              ? 'No missed appointments'
              : 'No appointments'}
          </p>
          {bookingFilter === 'upcoming' && (
            <button
              onClick={onBookNow}
              className="bg-secondary text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition font-semibold"
            >
              Book Now
            </button>
          )}
                      </div>
      ) : (
        <div className="space-y-3 px-4">
          {displayItems.map((item) => {
            if (item.kind === 'deal') {
              const expanded = isDealExpanded(item.id)
              const firstDay = item.days?.[0]
              const firstDate = firstDay ? formatDateForDisplay(firstDay.date) : ''
              const firstTime = firstDay ? formatTimeForDisplay(firstDay.time) : ''
              const primaryDay =
                [...(item.days || [])].sort(
                  (a, b) =>
                    (Number(b.totalAmount ?? b.total_amount) || 0) -
                    (Number(a.totalAmount ?? a.total_amount) || 0)
                )[0] || firstDay
              const dealStatus = String(primaryDay?.status || '').toLowerCase()
              const dealUpcoming =
                bookingFilter === 'upcoming' &&
                primaryDay &&
                ['upcoming', 'confirmed', 'pending'].includes(dealStatus) &&
                !isAppointmentExpired(primaryDay)
              const isCancellingDeal =
                primaryDay &&
                (cancellingBookingId === primaryDay.id ||
                  (item.days || []).some((d) => d.id === cancellingBookingId))

              return (
                <div
                  key={item.id}
                  id={`deal-card-${item.id}`}
                  className={`bg-white rounded-lg border-l-4 shadow-sm hover:shadow-md transition ${filterBorderClass}`}
                >
                  <div className="p-3 sm:p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-base text-gray-900">
                            {item.offerTitle || (item.isPackageOffer ? 'Package deal' : 'Multi-day booking')}
                          </h3>
                          <span className="inline-flex items-center text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full border border-primary/20">
                            {item.isPackageOffer ? 'Package' : 'Multi-day'}
                          </span>
                          {bookingFilter === 'cancelled' ? (
                            <span className="inline-flex items-center text-[10px] font-bold text-red-700 bg-red-50 px-2 py-0.5 rounded-full border border-red-200">
                              Cancelled
                            </span>
                          ) : null}
                          {bookingFilter === 'completed' ? (
                            <span className="inline-flex items-center text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                              Completed
                            </span>
                          ) : null}
                          {item.isRescheduled ? (
                            <span className="inline-flex items-center text-[10px] font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-200">
                              Rescheduled
                            </span>
                          ) : null}
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          {item.totalDays}-day {item.isPackageOffer ? 'package' : 'booking'} · {formatPriceFromUSD(item.packageTotal, currency)}
                        </p>
                        {!expanded && firstDay && (
                          <p className="text-xs text-gray-500 mt-1">
                            Starts {firstDate} at {firstTime}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleDealExpand(item.id)}
                        className="border border-gray-300 text-gray-700 py-1.5 px-3 rounded-lg hover:bg-gray-50 transition font-medium text-xs sm:text-sm flex items-center gap-1"
                      >
                        <i className={`fas fa-${expanded ? 'chevron-up' : 'chevron-down'} text-xs`}></i>
                        {expanded ? 'Hide days' : 'Show days'}
                      </button>
                    </div>

                    {bookingFilter === 'completed' && (() => {
                      const resolveDealPros =
                        getDealProfessionalsForReviewProp || getDealProfessionalsForReview
                      const resolveDealKey = getDealReviewKeyProp || getDealReviewKey
                      const seedDay = item.days?.[0] || null
                      const fullGroupDays =
                        seedDay && Array.isArray(allBookings) && allBookings.length > 0
                          ? findDealSiblingBookings(seedDay, allBookings, { includeCancelled: true })
                          : item.days || []
                      const packageFullyComplete = areAllPackageVisitsCompleted(fullGroupDays, {
                        totalDays: item.totalDays,
                      })
                      const reviewDays = packageFullyComplete ? fullGroupDays : item.days || []
                      const dealPros = resolveDealPros(reviewDays)
                      const dealKey = resolveDealKey(item.id)
                      if (!dealKey || dealPros.length === 0) {
                        if (!packageFullyComplete && (item.days || []).length > 0) {
                          return (
                            <div className="mt-3">
                              <p className="text-xs text-gray-500">
                                Review unlocks after all {item.totalDays || 'package'} visits are completed.
                              </p>
                            </div>
                          )
                        }
                        return null
                      }

                      const existingDealReviews = fullGroupDays.flatMap((day) => {
                        const dbApt = appointments?.find((apt) => apt.id === day.id)
                        if (Array.isArray(dbApt?.reviews)) return dbApt.reviews
                        if (Array.isArray(day.reviews)) return day.reviews
                        return []
                      })
                      const reviewedIds = new Set(
                        existingDealReviews
                          .map((r) => (r?.professional_id != null ? String(r.professional_id) : null))
                          .filter(Boolean)
                      )
                      const dealHasReview = dealPros.every((p) => reviewedIds.has(String(p.id)))
                      const dealShowReview = showReviewForBooking?.[dealKey]
                      const dealActiveProfId =
                        activeReviewTab?.[dealKey] || dealPros[0]?.id || null
                      const dealDraft =
                        (dealActiveProfId && reviewData?.[dealKey]?.[dealActiveProfId]) || {
                          rating: 0,
                          comment: '',
                        }
                      const dealSaved =
                        existingDealReviews.find(
                          (r) => String(r.professional_id) === String(dealActiveProfId)
                        ) || null
                      const dealActiveName =
                        dealPros.find((p) => String(p.id) === String(dealActiveProfId))?.name ||
                        'the professional'

                      return (
                        <div className="mt-3">
                          <div className="flex flex-wrap gap-2 mb-2">
                            {!packageFullyComplete && (
                              <p className="text-xs text-gray-500 w-full">
                                Review unlocks after all {item.totalDays || fullGroupDays.length} package visits are completed.
                              </p>
                            )}
                            {packageFullyComplete && !dealHasReview && !dealShowReview && (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!expanded) toggleDealExpand(item.id)
                                  toggleReview(dealKey, fullGroupDays)
                                }}
                                className="bg-secondary text-white py-1.5 px-3 rounded-lg hover:bg-gray-800 transition font-medium text-sm inline-flex items-center gap-1.5"
                              >
                                <i className="far fa-star text-xs"></i>
                                Leave a review
                              </button>
                            )}
                            {packageFullyComplete && dealHasReview && (
                              <button
                                type="button"
                                onClick={() => toggleReview(dealKey, fullGroupDays)}
                                className="bg-emerald-50 text-emerald-700 border border-emerald-200 py-1.5 px-3 rounded-lg hover:bg-emerald-100 transition font-medium text-sm inline-flex items-center gap-1.5"
                              >
                                <i className="fas fa-check-circle text-xs"></i>
                                {dealShowReview ? 'Hide review' : 'Reviewed'}
                              </button>
                            )}
                          </div>

                          {packageFullyComplete && dealShowReview && (
                            <div className="bg-gray-50 rounded-lg p-4 mb-3 border border-gray-200">
                              <div className="flex justify-between items-center mb-4">
                                <h4 className="font-semibold text-gray-900">
                                  {dealHasReview
                                    ? 'Your package review'
                                    : dealPros.length > 1
                                      ? 'Review your package'
                                      : 'Leave a review'}
                                </h4>
                                <button
                                  type="button"
                                  onClick={() => toggleReview(dealKey, fullGroupDays)}
                                  className="text-primary hover:underline text-sm font-medium"
                                >
                                  Hide review
                                </button>
                              </div>

                              {dealPros.length > 1 && (
                                <div className="flex flex-wrap gap-2 mb-3">
                                  {dealPros.map((prof) => {
                                    const isActive = String(prof.id) === String(dealActiveProfId)
                                    const draft = reviewData?.[dealKey]?.[prof.id] || {}
                                    const hasDraft =
                                      (draft.rating || 0) > 0 ||
                                      String(draft.comment || '').trim().length > 0 ||
                                      reviewedIds.has(String(prof.id))
                                    return (
                                      <button
                                        key={prof.id}
                                        type="button"
                                        onClick={() =>
                                          setActiveReviewProfessional?.(dealKey, prof.id)
                                        }
                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                                          isActive
                                            ? 'bg-secondary text-white border-secondary'
                                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                                        }`}
                                      >
                                        {prof.name}{hasDraft ? ' •' : ''}
                                      </button>
                                    )
                                  })}
                                </div>
                              )}

                              <p className="text-gray-700 mb-3">
                                How was your experience with {dealActiveName}?
                              </p>

                              {dealHasReview ? (
                                <>
                                  <div className="flex gap-1 mb-4">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <div key={star} className="text-2xl">
                                        <i
                                          className={`${
                                            star <= (dealSaved?.rating || 0)
                                              ? 'fas fa-star text-yellow-400'
                                              : 'far fa-star text-gray-300'
                                          }`}
                                        ></i>
                                      </div>
                                    ))}
                                  </div>
                                  <textarea
                                    value={dealSaved?.comment || ''}
                                    disabled
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 resize-none mb-2 cursor-not-allowed"
                                    rows="3"
                                  ></textarea>
                                </>
                              ) : (
                                <>
                                  <div className="flex gap-1 mb-4">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <button
                                        key={star}
                                        type="button"
                                        onClick={() =>
                                          handleReviewRating(dealKey, star, dealActiveProfId)
                                        }
                                        className="text-2xl focus:outline-none hover:scale-110 transition-transform cursor-pointer"
                                        disabled={!dealActiveProfId}
                                      >
                                        <i
                                          className={`${
                                            star <= (dealDraft.rating || 0)
                                              ? 'fas fa-star text-yellow-400'
                                              : 'far fa-star text-gray-300'
                                          }`}
                                        ></i>
                                      </button>
                                    ))}
                                  </div>
                                  <textarea
                                    value={dealDraft.comment || ''}
                                    onChange={(e) =>
                                      handleReviewComment(
                                        dealKey,
                                        e.target.value,
                                        dealActiveProfId
                                      )
                                    }
                                    placeholder={`Share your experience with ${dealActiveName}...`}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none mb-3"
                                    rows="3"
                                    disabled={!dealActiveProfId}
                                  ></textarea>
                                  {dealPros.length > 1 && (
                                    <p className="text-xs text-gray-500 mb-3">
                                      Switch tabs to review each professional from this package, then submit all at once.
                                    </p>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => handleSubmitReview(dealKey)}
                                    disabled={
                                      submittingReviewBookingId === dealKey || !dealActiveProfId
                                    }
                                    className={`bg-secondary text-white py-1.5 px-3 rounded-lg transition font-medium text-sm flex items-center gap-2 ${
                                      submittingReviewBookingId === dealKey
                                        ? 'opacity-50 cursor-not-allowed'
                                        : 'hover:bg-gray-800'
                                    }`}
                                  >
                                    {submittingReviewBookingId === dealKey ? (
                                      <>
                                        <i className="fas fa-spinner fa-spin"></i>
                                        <span>Submitting...</span>
                                      </>
                                    ) : dealPros.length > 1 ? (
                                      `Submit ${dealPros.length} reviews`
                                    ) : (
                                      'Submit review'
                                    )}
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })()}

                    {dealUpcoming && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        <button
                          type="button"
                          onClick={() => handleCancelAppointment(primaryDay.id)}
                          disabled={
                            !canCancelAppointment ||
                            !canCancelAppointment(primaryDay) ||
                            isCancellingDeal
                          }
                          className={`py-1.5 px-3 rounded-lg transition font-medium text-xs sm:text-sm flex items-center gap-1 ${
                            isCancellingDeal
                              ? 'bg-red-50 text-red-600 cursor-wait opacity-70'
                              : canCancelAppointment && canCancelAppointment(primaryDay)
                                ? 'bg-red-50 text-red-600 hover:bg-red-100'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                          title="Cancels all days of this package"
                        >
                          {isCancellingDeal ? (
                            <>
                              <i className="fas fa-spinner fa-spin text-xs"></i>
                              Cancelling...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-times text-xs"></i>
                              Cancel package
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (handleReschedule && !checkingRescheduleFee) {
                              setCheckingRescheduleFee(primaryDay.id)
                              try {
                                await handleReschedule(primaryDay, onCloseSidebar)
                              } catch (error) {
                                console.error('Error in handleReschedule:', error)
                              } finally {
                                setTimeout(() => setCheckingRescheduleFee(null), 500)
                              }
                            }
                          }}
                          disabled={checkingRescheduleFee === primaryDay.id}
                          className={`py-1.5 px-3 rounded-lg transition font-medium text-xs sm:text-sm flex items-center gap-1 ${
                            checkingRescheduleFee === primaryDay.id
                              ? 'bg-blue-100 text-blue-700 cursor-wait opacity-70'
                              : canRescheduleAppointment && canRescheduleAppointment(primaryDay)
                                ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                                : 'bg-gray-100 text-gray-400 cursor-pointer'
                          }`}
                          title="Reschedule all days of this package"
                        >
                          {checkingRescheduleFee === primaryDay.id ? (
                            <>
                              <i className="fas fa-spinner fa-spin text-xs"></i>
                              Checking...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-calendar-alt text-xs"></i>
                              Reschedule package
                            </>
                          )}
                        </button>
                  </div>
                    )}

                    {dealUpcoming && (() => {
                      const cancelOk = canCancelAppointment ? canCancelAppointment(primaryDay) : true
                      const rescheduleOk = canRescheduleAppointment ? canRescheduleAppointment(primaryDay) : true
                      if (cancelOk && rescheduleOk) return null
                      let message = 'Cancellation/rescheduling is not available for this package'
                      if (!cancelOk && rescheduleOk) {
                        message = 'Cancellation is turned off for this offer'
                      } else if (cancelOk && !rescheduleOk) {
                        const blockReason = getRescheduleBlockReason
                          ? getRescheduleBlockReason(primaryDay)
                          : null
                        message =
                          blockReason === 'offerBlocked'
                            ? 'Rescheduling is turned off for this offer'
                            : blockReason === 'maxReschedules'
                              ? 'You have already used all allowed reschedules for this package'
                              : 'Rescheduling is not available for this package'
                      } else if (!cancelOk && !rescheduleOk) {
                        message = 'Cancellation and rescheduling are turned off for this offer'
                      }
                      return (
                        <p className="text-sm text-red-600 font-medium mb-2">{message}</p>
                      )
                    })()}

                    {expanded && (
                      <div className="space-y-2 mt-3">
                        {(item.days || []).map((day) =>
                          renderBookingCard(day, {
                            cardTitle: `Day ${day.day_number} of ${item.totalDays}`,
                            nested: true,
                            inDeal: true,
                          })
                        )}
                      </div>
                    )}
                </div>
              </div>
            )
            }

            return renderBookingCard(item.booking, { cardTitle: 'Appointment' })
          })}
        </div>
      )}
    </div>
  )
}

export default BookingsTab
